import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import * as controllers from 'controllers'
import { VehicleDto, VehicleExporterInformationDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import ExporterInformationAddEditDialog from './ExporterInformationAddEditDialog'
import { formatAddress } from 'utils'

interface ExporterInformationListCardprops {
  vehicle: VehicleDto
}
export default function ExporterInformationListCard({ vehicle }: ExporterInformationListCardprops) {
  const [isSavingExporterInformation, setIsSavingExporterInformation] = useState<boolean>(false)
  const [isGettingExporterInformation, setIsGettingExporterInformation] = useState<boolean>(false)
  const [ExporterInformationAddEditDialogOpen, setExporterInformationAddEditDialogOpen] = useState<boolean>(false)
  const [exporterInformation, setExporterInformation] = useState<VehicleExporterInformationDto>(
    new VehicleExporterInformationDto()
  )

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingExporterInformation(true)
      controllers
        .getExporterInformation(vehicle.vehicleGuid!)
        .then(response => {
          setExporterInformation(response.value)
        })
        .finally(() => {
          setIsGettingExporterInformation(false)
        })
    }
  }, [vehicle])

  return (
    <>
      <ExporterInformationAddEditDialog
        disabled={Boolean(vehicle.vehicleGuid && vehicle.isLocked)}
        initialValues={exporterInformation}
        isLoading={isSavingExporterInformation}
        onClose={() => {
          setExporterInformationAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsSavingExporterInformation(true)

          controllers
            .updateExporterInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              setExporterInformation(response.value)
              setExporterInformationAddEditDialogOpen(false)
              enqueueSnackbar('Exporter Information Saved Successfully!', { variant: 'success' })
            })
            .finally(() => {
              setIsSavingExporterInformation(false)
            })
        }}
        open={ExporterInformationAddEditDialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Exporter Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    setExporterInformationAddEditDialogOpen(true)
                  }}
                  variant='text'
                  endIcon={<Edit />}
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingExporterInformation && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingExporterInformation && (
              <>
                <Grid item xs={6}>
                  <LabelField
                    label='Exporter Mileage'
                    value={
                      exporterInformation.exporterMileage && exporterInformation.exporterMileageUnitOfMeasurement
                        ? `${exporterInformation.exporterMileage} ${exporterInformation.exporterMileageUnitOfMeasurement}`
                        : ''
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <LabelField label='Buyer Company' value={exporterInformation.buyerCompany?.name} />
                </Grid>

                <Grid item xs={6}>
                  <LabelField label='Title Processing State' value={exporterInformation.titleProcessingState?.name} />
                </Grid>

                <Grid item xs={6}>
                  <LabelField label='Title Destination Company' value={exporterInformation.titleDestinationCompany?.name} />
                </Grid>

                <Grid item xs={6}>
                  <LabelField
                    label='Title Destination Company Address'
                    value={
                      exporterInformation.titleDestinationCompanyAddress
                        ? formatAddress(exporterInformation.titleDestinationCompanyAddress)
                        : ''
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <LabelField label='Vehicle Destination Company' value={exporterInformation.vehicleDestinationCompany?.name} />
                </Grid>

                <Grid item xs={6}>
                  <LabelField
                    label='Vehicle Destination Company Address'
                    value={
                      exporterInformation.vehicleDestinationCompanyAddress
                        ? formatAddress(exporterInformation.vehicleDestinationCompanyAddress)
                        : ''
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <LabelField label='Convert Cluster' value={exporterInformation.exporterConvertCluster ? 'Yes' : 'No'} />
                </Grid>

                <Grid item xs={6}>
                  <LabelField
                    label='Previous US Vehicle - Box 2A'
                    value={exporterInformation.isPreviousUSVehicleBox2A ? 'Yes' : 'No'}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sx={{ mt: 2 }}>
              <GreenText variant='outlined'>
                {`**Exporter Information:**

Information from this card will be displayed to users during the Vehicle Check-In process.

---`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
